import React, { useRef, useState, useEffect } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  GRID_COLS,
  GRID_ROW_HEIGHT_PX,
  GRID_MARGIN,
} from "features/analytics/studio/utils/boardLayoutUtils";
import { BORDERED_CARD_TYPES, EDITOR_CARD_TYPES, getCardComponent } from "./boardCardRegistry";

// CSS vars work in <style> tags — drag/resize states use brand tokens for both themes
const GRID_STYLES = `
  .board-v2 .react-grid-item.react-grid-placeholder {
    background: color-mix(in srgb, var(--brand-primary) 8%, transparent) !important;
    border: 1.5px dashed color-mix(in srgb, var(--brand-primary) 40%, transparent) !important;
    border-radius: 12px !important;
  }
  .board-v2 .react-grid-item > .react-resizable-handle {
    opacity: 0;
    transition: opacity .15s;
  }
  .board-v2 .react-grid-item:hover > .react-resizable-handle {
    opacity: .45;
  }
  .board-v2 .react-grid-item.react-draggable-dragging {
    box-shadow: 0 8px 28px rgba(0,0,0,.14) !important;
    z-index: 100 !important;
  }
`;

export default function BoardCanvas({
  cards = [],
  layout = [],
  cardResults = {},
  readOnly = false,
  onLayoutChange,
  onEdit,
  onDelete,
  onSaveTextContent,
  onSaveMediaUrl,
  onSaveDisplayOptions,
  boardId,
  homeWorkspaceId,
}) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setContainerWidth(w);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <style>{GRID_STYLES}</style>
      <div ref={containerRef} className="board-v2 w-full relative" style={{ minHeight: 200 }}>
        <GridLayout
          layout={layout}
          cols={GRID_COLS}
          rowHeight={GRID_ROW_HEIGHT_PX}
          width={containerWidth}
          margin={GRID_MARGIN}
          containerPadding={[0, 0]}
          onLayoutChange={onLayoutChange}
          isDraggable={!readOnly}
          isResizable={!readOnly}
          compactType="vertical"
          preventCollision={false}
          draggableHandle=".board-drag-handle"
          resizeHandles={["se"]}
          useCSSTransforms
        >
          {cards.map((card) => {
            const cardType = (card?.cardType || "insights").toLowerCase();
            const { Component } = getCardComponent(cardType);
            const isBordered = BORDERED_CARD_TYPES.has(cardType);
            const canEdit = EDITOR_CARD_TYPES.has(cardType);

            const cardProps = {
              card,
              result: cardResults[card.id],
              readOnly,
              onDelete: !readOnly && onDelete ? () => onDelete(card.id) : undefined,
              onEdit: !readOnly && canEdit && onEdit ? () => onEdit(card) : undefined,
              onSaveContent: !readOnly && onSaveTextContent
                ? (payload) => onSaveTextContent(card.id, payload)
                : undefined,
              onSaveUrl: !readOnly && onSaveMediaUrl
                ? (url, type) => onSaveMediaUrl(card.id, url, type)
                : undefined,
              onSaveDisplayOptions: !readOnly && onSaveDisplayOptions
                ? (opts) => onSaveDisplayOptions(card.id, opts)
                : undefined,
              boardId,
              homeWorkspaceId,
            };

            return (
              <div
                key={String(card.id)}
                className={[
                  "rounded-xl overflow-hidden h-full",
                  isBordered ? "border border-[color:var(--border-default)]" : "",
                ].join(" ")}
                style={
                  isBordered
                    ? { backgroundColor: "var(--surface-base)" }
                    : { backgroundColor: "transparent" }
                }
              >
                <Component {...cardProps} />
              </div>
            );
          })}
        </GridLayout>
      </div>
    </>
  );
}
